import { createElement } from '../utils/dom.js';

const badgeClassMap = {
  hot: 'badge--hot',
  new: 'badge--new',
  'editor-pick': 'badge--editor-pick',
  featured: 'badge--featured',
};

const badgeLabelMap = {
  hot: 'Trending Now',
  new: 'Fresh Drop',
  'editor-pick': 'WaHaGames Approved',
  featured: 'Spotlight Pick',
};

const badgeLabel = badge => {
  if (!badge) return '';
  const key = String(badge).toLowerCase();
  if (badgeLabelMap[key]) {
    return badgeLabelMap[key];
  }
  return String(badge)
    .replace(/[_-]+/g, ' ')
    .toUpperCase();
};

const formatPlays = value => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return value.toString();
};

const getGameStatus = game => {
  const now = new Date();

  // Check if NEW (published within 7 days)
  if (game.published) {
    const published = new Date(game.published);
    if (!isNaN(published.getTime())) {
      const daysSince = (now - published) / (1000 * 60 * 60 * 24);
      if (daysSince <= 7) {
        return 'new';
      }
    }
  }

  // Check if UPDATED (updatedAt within 7 days, but not new)
  if (game.updatedAt) {
    const updated = new Date(game.updatedAt);
    if (!isNaN(updated.getTime())) {
      const daysSince = (now - updated) / (1000 * 60 * 60 * 24);
      if (daysSince <= 7) {
        return 'updated';
      }
    }
  }

  // Check if HOT (top 10% by plays - threshold ~29000)
  const hotThreshold = 29000;
  if (game.plays && game.plays >= hotThreshold) {
    return 'hot';
  }

  return null;
};

const renderBadges = (badges = []) => {
  if (!badges.length) return '';
  const items = badges
    .map(badge => `<li class="badge ${badgeClassMap[badge] || ''}">${badgeLabel(badge)}</li>`)
    .join('');
  return `<ul class="game-card__badges">${items}</ul>`;
};

const gameCardTemplate = game => {
  const attributes = [
    `data-categories="${(game.categories || []).join(' ')}"`,
    `data-tags="${(game.tags || []).join(' ')}"`,
    `data-title="${game.title}"`,
    `data-rating="${game.rating || 0}"`,
    `data-plays="${game.plays || 0}"`,
    `data-published="${game.published}"`,
  ].join(' ');

  const primaryCategory = (game.categories || [])[0] || '';
  const formattedCategory = primaryCategory
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
  const rawProvider = (game.provider || '').trim();
  const hiddenProviders = ['gamemonetize', 'gamemonetize.com'];
  const providerLabel = rawProvider && !hiddenProviders.includes(rawProvider.toLowerCase()) ? rawProvider : '';

  const formattedRating = (game.rating || 0).toFixed(1).replace(/\.0$/, '');
  const gameStatus = getGameStatus(game);
  const metaParts = [];
  if (providerLabel) {
    metaParts.push(`<span class="game-card__provider">${providerLabel}</span>`);
  }
  if (formattedCategory) {
    const categoryMarkup = `<span class="game-card__category">${formattedCategory}</span>`;
    metaParts.push(metaParts.length ? `<span class="game-card__meta-separator" aria-hidden="true">&#183;</span>${categoryMarkup}` : categoryMarkup);
  }
  const metaMarkup = metaParts.join('') || '';

  return `<article class="game-card" data-game ${attributes}>
    <a class="game-card__link" href="/game/${game.slug}/" aria-label="Play ${game.title}">
      <div class="game-card__thumb">
        <img src="${game.thumb}" loading="lazy" alt="${game.title} thumbnail" width="320" height="180" />
        ${gameStatus ? `<span class="game-card__status-badge game-card__status-badge--${gameStatus}">${gameStatus.toUpperCase()}</span>` : ''}
        ${renderBadges(game.badges)}
        <span class="game-card__overlay" aria-hidden="true">Play &#9654;</span>
      </div>
      <div class="game-card__body">
        <h3 class="game-card__title">${game.title}</h3>
        <p class="game-card__meta">${metaMarkup}</p>
        <div class="game-card__stats">
          <span class="game-card__rating"><span aria-hidden="true">&#9733;</span> ${formattedRating}</span>
          <span class="game-card__plays">${formatPlays(game.plays || 0)} plays</span>
        </div>
      </div>
    </a>
  </article>`;
};

export const renderGames = (container, games) => {
  if (!container) return;
  const fragment = document.createDocumentFragment();
  games.forEach(game => {
    fragment.appendChild(createElement(gameCardTemplate(game)));
  });
  container.innerHTML = '';
  container.appendChild(fragment);
};
